/**
 * Email Collector Section JavaScript
 * Handles form interactions, accessibility, and user experience enhancements
 */

class EmailCollector {
  constructor(container) {
    this.container = container;
    this.form = container.querySelector('.email-collector__form-inner');
    this.input = container.querySelector('.email-collector__input');
    this.button = container.querySelector('.email-collector__button');
    
    this.init();
  }

  init() {
    if (!this.form || !this.input || !this.button) {
      console.warn('Email Collector: Required form elements not found');
      return;
    }

    this.setupEventListeners();
    this.setupAccessibility();
    this.setupFormValidation();
  }

  setupEventListeners() {
    // Form submission
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    
    // Input validation on blur
    this.input.addEventListener('blur', this.validateEmail.bind(this));
    
    // Input validation on input (real-time)
    this.input.addEventListener('input', this.handleInput.bind(this));
    
    // Keyboard navigation
    this.input.addEventListener('keydown', this.handleKeydown.bind(this));
    
    // Button click
    this.button.addEventListener('click', this.handleButtonClick.bind(this));
  }

  setupAccessibility() {
    // Ensure proper ARIA attributes
    this.input.setAttribute('aria-describedby', `Email-error-${this.container.id || 'default'}`);
    
    // Add role to form
    this.form.setAttribute('role', 'form');
    this.form.setAttribute('aria-label', 'Email subscription form');
    
    // Ensure button has proper type
    this.button.setAttribute('type', 'submit');
  }

  setupFormValidation() {
    // Add HTML5 validation attributes
    // Note: Shopify will handle server-side validation
    this.input.setAttribute('required', 'true');
    this.input.setAttribute('pattern', '[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$');
  }

  handleSubmit(event) {
    // Basic HTML5 validation check
    if (!this.input.checkValidity()) {
      // Let HTML5 validation handle it
      return;
    }

    const email = this.input.value.trim();
    if (!email) {
      event.preventDefault();
      return;
    }

    // Set loading state for visual feedback
    // Don't disable the form - let it submit normally
    const originalButtonText = this.button.textContent;
    this.button.setAttribute('data-original-text', originalButtonText);
    this.button.textContent = 'Subscribing...';
    this.button.style.opacity = '0.7';
    
    // Let the form submit naturally to Shopify's customer endpoint
    // The form will reload and show success/error message from Shopify
  }

  handleInput(event) {
    // Clear previous error states (only client-side errors)
    const clientError = this.form.querySelector('.email-collector__message--error:not([data-server-error])');
    if (clientError) {
      clientError.remove();
    }
    
    // Real-time validation feedback (only visual, don't block)
    if (event.target.value.length > 0) {
      this.input.removeAttribute('aria-invalid');
    }
  }

  handleKeydown(event) {
    // Allow Enter key to submit form naturally
    // Don't prevent default - let the form submit normally
    // The handleSubmit will validate but won't block valid submissions
  }

  handleButtonClick(event) {
    // Prevent double submission
    if (this.button.disabled) {
      event.preventDefault();
      return;
    }
  }

  validateEmail() {
    const email = this.input.value.trim();
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    // Use HTML5 validation instead of custom validation
    if (!this.input.checkValidity()) {
      return false;
    }
    
    if (!email) {
      return false;
    }
    
    if (!emailRegex.test(email)) {
      return false;
    }
    
    return true;
  }

  showError(message) {
    // Only show client-side errors if no server error exists
    const serverError = this.form.querySelector('.email-collector__message--error[data-server-error]');
    if (serverError) {
      return; // Server errors take precedence
    }
    
    this.clearClientError();
    
    const errorDiv = document.createElement('div');
    errorDiv.className = 'email-collector__message email-collector__message--error';
    errorDiv.id = `Email-error-${this.container.id || 'default'}`;
    errorDiv.setAttribute('role', 'alert');
    errorDiv.setAttribute('aria-live', 'polite');
    
    errorDiv.innerHTML = `
      <span class="visually-hidden">Error: </span>
      <span class="email-collector__error-icon">⚠</span>
      <span class="email-collector__error-message">${message}</span>
    `;
    
    this.form.appendChild(errorDiv);
    
    // Focus on input for screen readers
    this.input.focus();
    this.input.setAttribute('aria-invalid', 'true');
  }

  clearErrorState() {
    // Clear both client and check for server errors
    this.clearClientError();
  }
  
  clearClientError() {
    const existingError = this.form.querySelector('.email-collector__message--error:not([data-server-error])');
    if (existingError) {
      existingError.remove();
    }
    
    // Only remove aria-invalid if no server error
    const serverError = this.form.querySelector('.email-collector__message--error[data-server-error]');
    if (!serverError) {
      this.input.removeAttribute('aria-invalid');
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      // Only visual feedback, don't disable form
      const originalButtonText = this.button.textContent;
      this.button.setAttribute('data-original-text', originalButtonText);
      this.button.textContent = 'Subscribing...';
      this.button.style.opacity = '0.7';
    } else {
      this.button.textContent = this.button.getAttribute('data-original-text') || 'Join the movement';
      this.button.style.opacity = '1';
    }
  }

  // Public method to reset form
  resetForm() {
    this.form.reset();
    this.clearErrorState();
    this.setLoadingState(false);
  }
}

// Initialize Email Collector when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const emailCollectors = document.querySelectorAll('.email-collector');
  
  emailCollectors.forEach(container => {
    new EmailCollector(container);
  });
});

// Handle form success/error states from server
document.addEventListener('DOMContentLoaded', function() {
  const forms = document.querySelectorAll('.email-collector__form-inner');
  
  forms.forEach(form => {
    // Check if form was submitted successfully
    if (form.querySelector('.email-collector__message--success')) {
      // Clear form after successful submission
      setTimeout(() => {
        form.reset();
      }, 3000);
    }
  });
});

// Export for potential external use
window.EmailCollector = EmailCollector;
